import React, { useMemo } from 'react';
import { Card } from '../../Library/Card';
import { Box, Typography } from '@mui/material';
import { RootineDivider } from '../../../atoms/RootineDivider';
import {
  CortisolResult,
  HormoneSnapshot,
  StressInsightsColors,
} from '../../../types';
import { roundNumber } from '../../../utils';
import { DescriptorData } from './CortisolMetricsDescriptor';
import { colors, theme } from '../../../utils/theme';

type CortisolRow = {
  time: string;
  waking: CortisolResult;
  midday: CortisolResult;
  bedtime: CortisolResult;
};

enum SampleTimes {
  'waking',
  'midday',
  'bedtime',
}

const ResultBox = ({ data }: { data: CortisolResult }) => {
  const determineDescriptorData = (
    resultData: CortisolResult
  ): DescriptorData => {
    const { result, high, low } = resultData;
    if (result > high) {
      return {
        color: StressInsightsColors.EXHAUSTED,
        label: 'High',
      };
    }
    if (result < low) {
      return {
        color: StressInsightsColors.ALARMED,
        label: 'Low',
      };
    }
    return {
      color: StressInsightsColors.NORMAL,
      label: 'Normal',
    };
  };

  const descriptorData = useMemo(() => determineDescriptorData(data), [data]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      sx={{ flex: '0 0 25%' }}
    >
      <Typography variant="mono" fontSize={12}>
        {data?.result}
      </Typography>
      <Box
        sx={{
          background: descriptorData.color,
          px: 0.25,
          borderRadius: 0.5,
          color: colors.greys.BASE_WHITE,
          mt: 0.5,
        }}
      >
        <Typography sx={{ fontWeight: theme.typography.mono }} fontSize={10}>
          {descriptorData.label}
        </Typography>
      </Box>
    </Box>
  );
};

export const CortisolHistoryCard = ({
  snapshots,
}: {
  snapshots: HormoneSnapshot[];
}) => {
  const extractAndLabelData = (snapshot: HormoneSnapshot): CortisolRow => {
    const data: { [key: string]: CortisolResult } = {};

    snapshot.test_results.forEach((result, idx) => {
      const timeLabel = SampleTimes[idx] || `sample ${idx + 1}`;
      const averageValue = roundNumber(
        (result.min_value + result.max_value) / 2
      );

      data[timeLabel] = {
        time: timeLabel,
        result: roundNumber(result.result),
        low: roundNumber(result.min_value),
        average: averageValue,
        high: roundNumber(result.max_value),
      };
    });

    return {
      time: snapshot.sample_date,
      waking: data.waking,
      midday: data.midday,
      bedtime: data.bedtime,
    };
  };

  const rows: CortisolRow[] = useMemo(
    () => snapshots.map(extractAndLabelData),
    [snapshots]
  );

  const formatDateWithDots = (dateString: string) => {
    return dateString.replace(/-/g, '.');
  };

  return (
    <Box sx={{ my: 3 }}>
      <Card
        title="Cortisol history"
        content={
          <Box>
            <RootineDivider dividerProps={{ sx: { mt: 1, mb: 2 } }} />
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography
                variant="light"
                fontSize={14}
                sx={{ flex: '0 0 25%' }}
              >
                Date
              </Typography>
              <Typography
                variant="light"
                fontSize={14}
                sx={{ flex: '0 0 25%' }}
              >
                Waking
              </Typography>
              <Typography
                variant="light"
                fontSize={14}
                sx={{ flex: '0 0 25%' }}
              >
                Mid-day
              </Typography>
              <Typography
                variant="light"
                fontSize={14}
                sx={{ flex: '0 0 25%' }}
              >
                Bedtime
              </Typography>
            </Box>
            {rows.map((row, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="mono"
                  fontSize={12}
                  sx={{ flex: '0 0 25%' }}
                >
                  {formatDateWithDots(row.time)}
                </Typography>
                <ResultBox data={row.waking} />
                <ResultBox data={row.midday} />
                <ResultBox data={row.bedtime} />
              </Box>
            ))}
          </Box>
        }
      />
    </Box>
  );
};
